import {
  alpha,
  avatarClasses,
  badgeClasses,
  buttonBaseClasses,
  chipClasses,
  inputClasses,
  touchRippleClasses
} from "@mui/material";
import { createDarkPalette } from "./colors/createDarkPalette.js";
import { createLightPalette } from "./colors/createLightPalette.js";
import { createShadows } from "./colors/createShadows.js";
import { createTidalColorPalettes } from "./colors/createTidalColorPalettes.js";
import { createChipFilledOutlineVariantStyles } from "./components/chip.js";
function createTidalThemeOptions(colorMode) {
  const { tidalColorsDark, tidalColorsLight } = createTidalColorPalettes();
  const tidalColors = colorMode === "light" ? tidalColorsLight : tidalColorsDark;
  return {
    shadows: createShadows(8, tidalColors, colorMode),
    typography: {
      button: {
        textTransform: "none"
      },
      h7: {
        fontSize: "1.125rem",
        letterSpacing: "0.15px",
        fontWeight: 700,
        lineHeight: 1.2
      },
      subtitle3: {
        fontSize: "1rem",
        letterSpacing: "0.15px",
        fontWeight: 500,
        lineHeight: 1.5
      },
      subtitle4: {
        fontSize: "0.875rem",
        letterSpacing: "0.25px",
        fontWeight: 700,
        lineHeight: 1.43
      }
    },
    components: {
      MuiButton: {
        variants: [
          {
            // see: https://workwave.atlassian.net/browse/CUI-234
            props: { color: "error" },
            style: ({ theme }) => ({
              [`.${touchRippleClasses.rippleVisible}`]: {
                opacity: 0.2,
                backgroundColor: theme.vars.palette.tidalColors.lobsterRed[500]
              }
            })
          }
        ]
      },
      MuiBadge: {
        variants: [
          {
            props: {
              variant: "dotSpaced"
            },
            style: {
              [`& .${badgeClasses.badge}`]: {
                color: "transparent",
                transform: "scale(0.4) translate(calc(50% + 6px), calc(-50% - 6px))"
              }
            }
          }
        ]
      },
      MuiBackdrop: {
        styleOverrides: {
          root: ({ theme }) => ({
            // intentionally not using theme.vars, channel does not exist for slateBlueGray
            backgroundColor: alpha(theme.palette.tidalColors.slateBlueGray.A700, 0.55)
          }),
          invisible: {
            backgroundColor: "transparent"
          }
        }
      },
      MuiCardActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2),
            paddingTop: theme.spacing(1)
          })
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2)
          })
        }
      },
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingBottom: theme.spacing(1)
          })
        }
      },
      MuiDialog: {
        defaultProps: {
          PaperProps: {
            elevation: 7
          }
        }
      },
      MuiIconButton: {
        variants: [
          {
            props: {
              size: "tiny"
            },
            style: {
              padding: 0,
              fontSize: "16px"
            }
          }
        ]
      },
      MuiMenu: {
        defaultProps: {
          PaperProps: {
            elevation: 6
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            [`& .${inputClasses.disabled}`]: {
              backgroundColor: theme.vars.palette.grey["100"]
            }
          })
        }
      },
      MuiTooltip: {
        styleOverrides: {
          arrow: ({ theme }) => ({
            color: theme.palette.text?.contrastText
          }),
          popperArrow: ({ theme }) => ({
            color: theme.palette.text?.contrastText
          }),
          tooltip: ({ theme }) => ({
            backgroundColor: colorMode === "light" ? theme.palette.text?.contrastText : theme.palette.background.paper
          })
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ ownerState }) => {
            const sizeMedium = ownerState?.className?.includes("MuiInputLabel-sizeMedium");
            const shrink = ownerState?.["data-shrink"] === true;
            return {
              ...sizeMedium && shrink && {
                // can't find another method to target both size medium and shrink
                // if !important is not set, the style is overridden by the default style
                transform: "translate(14px, -9px) scale(0.875) !important"
              }
            };
          }
        }
      },
      MuiOutlinedInput: {
        variants: [
          {
            props: {
              size: "medium"
            },
            style: {
              legend: {
                fontSize: "0.875rem"
              }
            }
          }
        ]
      },
      MuiSelect: {
        styleOverrides: {
          root: ({ theme }) => ({
            [`&.${inputClasses.disabled}`]: {
              backgroundColor: theme.vars.palette.grey["100"]
            }
          })
        }
      },
      MuiRating: {
        styleOverrides: {
          label: ({ theme }) => ({
            color: theme.vars.palette.warning.main
          }),
          readOnly: ({ theme }) => ({
            color: theme.vars.palette.warning.main
          })
        }
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            [`& .${buttonBaseClasses.root}`]: {
              backgroundColor: "transparent"
            }
          }
        }
      },
      MuiAvatarGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            [`& .${avatarClasses.root}`]: {
              borderColor: `var(--avatar-border-color, ${theme.vars.palette.tidalColors.slateBlueGray[100]})`
            }
          })
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.vars.palette.tidalColors.slateBlueGray[600]
          })
        },
        variants: [
          {
            props: {
              variant: "outlined"
            },
            // the css variables makes easier to override the border color
            // in the MuiAvatarGroup styleOverrides because the className generated
            // by the `outlinedRed` variant is not inside the avatarClasses object
            style: ({ theme }) => ({
              "--avatar-border-color": theme.vars.palette.tidalColors.slateBlueGray[100],
              border: `2px solid var(--avatar-border-color)`
            })
          },
          {
            props: {
              variant: "outlinedRed"
            },
            style: ({ theme }) => ({
              "--avatar-border-color": theme.vars.palette.tidalColors.lobsterRed[500],
              border: `2px solid var(--avatar-border-color)`
            })
          }
        ]
      },
      MuiChip: {
        styleOverrides: {
          iconMedium: {
            fontSize: "1.25rem"
          },
          iconSmall: {
            fontSize: "1.25rem"
          },
          sizeMedium: ({ theme }) => ({
            fontSize: theme.typography.body2.fontSize
          }),
          root: ({ theme, ownerState }) => {
            return {
              color: theme.vars.palette.text.primary,
              [`& .${chipClasses.deleteIcon}`]: {
                color: theme.vars.palette.action.active,
                marginLeft: theme.spacing(-1)
              },
              [`& .${chipClasses.deleteIconSmall}`]: {
                marginLeft: theme.spacing(-0.5)
              },
              [`& .${chipClasses.avatar}`]: {
                color: theme.vars.palette.text.primary
              },
              ...ownerState?.color === "error" && ownerState?.variant === "filled" && {
                color: theme.vars.palette.primary.contrastText
              },
              ...(ownerState?.color === "error" || ownerState?.color === "success" || ownerState?.color === "warning") && ownerState?.variant === "filled" && {
                [`& .${chipClasses.icon}`]: {
                  color: theme.vars.palette.primary.contrastText
                }
              },
              ...ownerState?.color === "error" && ownerState?.variant === "filled" && {
                [`& .${chipClasses.deleteIcon}:hover`]: {
                  color: `rgba(${theme.vars.palette.action.activeChannel} / 0.87)`
                }
              },
              ...ownerState?.variant === "filled" && {
                [`& .${chipClasses.avatar}`]: {
                  backgroundColor: theme.vars.palette.primary.contrastText
                }
              },
              ...ownerState?.variant === "filled" && ownerState?.color === "primary" && {
                [`& .${chipClasses.avatar}`]: {
                  color: theme.vars.palette.text.primary
                },
                [`& .${chipClasses.avatarColorPrimary}`]: {
                  backgroundColor: theme.vars.palette.primary.contrastText
                }
              },
              ...(ownerState?.color === "error" || ownerState?.color === "success" || ownerState?.color === "warning") && ownerState?.variant === "outlined" && {
                [`& .${chipClasses.icon}`]: {
                  color: theme.vars.palette[ownerState.color].main
                }
              },
              ...ownerState?.color === "primary" && ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatar}`]: {
                  // should be avatar-fill but in our codebase this variable does not exist
                  backgroundColor: theme.vars.palette.grey[400],
                  color: theme.vars.palette.text.primary
                }
              },
              ...ownerState?.color === "secondary" && ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatar}`]: {
                  // should be avatar-fill but in our codebase this variable does not exist
                  backgroundColor: theme.vars.palette.grey[400]
                }
              },
              ...Boolean(ownerState?.disabled) && {
                // if !important is not set, the style is overridden
                opacity: "1 !important",
                color: theme.vars.palette.text.disabled,
                borderColor: theme.vars.palette.action.disabled,
                backgroundColor: theme.vars.palette.action.disabledBackground,
                [`& .${chipClasses.icon}`]: {
                  color: theme.vars.palette.action.disabled
                },
                [`& .${chipClasses.deleteIcon}`]: {
                  color: theme.vars.palette.action.disabled
                },
                [`& .${chipClasses.deleteIcon}:hover`]: {
                  color: theme.vars.palette.action.disabled
                },
                [`& .${chipClasses.avatar}`]: {
                  color: theme.vars.palette.action.disabled,
                  backgroundColor: theme.vars.palette.action.disabledBackground
                }
              },
              ...ownerState?.variant === "outlined" && Boolean(ownerState?.disabled) && {
                backgroundColor: "transparent"
              },
              // change avatar background color to match default color
              // for primary and secondary color, the others are fine as it is
              ...ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatarColorPrimary}`]: {
                  backgroundColor: theme.vars.palette.tidalColors.slateBlueGray[600]
                },
                [`& .${chipClasses.avatarColorSecondary}`]: {
                  backgroundColor: theme.vars.palette.tidalColors.slateBlueGray[600]
                }
              },
              ...ownerState?.variant === "outlined" && {
                [`& .${chipClasses.avatar}`]: {
                  color: theme.palette.primary.contrastText
                }
              }
            };
          }
        },
        variants: createChipFilledOutlineVariantStyles()
      }
    },
    colorSchemes: {
      light: {
        palette: createLightPalette(tidalColorsLight)
      },
      dark: {
        palette: createDarkPalette(tidalColorsDark)
      }
    }
  };
}
export {
  createTidalThemeOptions
};
//# sourceMappingURL=createTidalThemeOptions.js.map
